// src/utils/exchangeRateService.js
// Live exchange rates (1-hour cache) + country / currency helpers.
//
// MODEL USED EVERYWHERE
//   * A shop's BASE currency is the currency of the shop's country
//     (Pakistan -> PKR, United Kingdom -> GBP ...). Prices are stored in it.
//   * Rates are fetched relative to one base: rates[X] = how many X you get
//     for 1 base. Any pair can then be converted with
//         rate(from -> to) = rates[to] / rates[from]

import { COUNTRY_CURRENCY } from './countryCurrency';

const CACHE_KEY = 'exchange_rates_cache_v3';
const CACHE_DURATION_MS = 60 * 60 * 1000; // 1 hour
const REQUEST_TIMEOUT_MS = 8000;

const PRIMARY_API = (base) => `https://api.exchangerate.fun/latest?base=${base}`;
const FALLBACK_API = (base) => `https://open.er-api.com/v6/latest/${base}`;

// ------------------------------------------------------------------
// Country / currency tables (generated from the backend file)
// ------------------------------------------------------------------

/** { 'Pakistan': 'PKR', ... } (kept for old imports) */
export const COUNTRY_TO_CURRENCY_CODE = Object.fromEntries(
  Object.entries(COUNTRY_CURRENCY).map(([country, [code]]) => [country, code])
);

/** { 'PKR': 'Rs.', ... } */
export const CURRENCY_SYMBOLS = {};
/** { 'PKR': 'Pakistani Rupee', ... } */
export const CURRENCY_NAMES = {};
/** { 'EUR': ['Andorra', 'Austria', ...], ... } */
export const CURRENCY_COUNTRIES = {};

Object.entries(COUNTRY_CURRENCY).forEach(([country, [code, symbol, name]]) => {
  if (!CURRENCY_SYMBOLS[code]) CURRENCY_SYMBOLS[code] = symbol;
  if (!CURRENCY_NAMES[code]) CURRENCY_NAMES[code] = name;
  (CURRENCY_COUNTRIES[code] = CURRENCY_COUNTRIES[code] || []).push(country);
});

/** Currency code of a country name, or null. */
export const getCurrencyCodeFromCountry = (country) => {
  if (!country) return null;
  const key = String(country).trim();
  if (COUNTRY_TO_CURRENCY_CODE[key]) return COUNTRY_TO_CURRENCY_CODE[key];

  // tolerate different upper / lower case
  const lower = key.toLowerCase();
  const match = Object.keys(COUNTRY_TO_CURRENCY_CODE).find((c) => c.toLowerCase() === lower);
  return match ? COUNTRY_TO_CURRENCY_CODE[match] : null;
};

export const getCurrencySymbol = (code) => {
  if (!code) return '';
  const upper = String(code).toUpperCase();
  return CURRENCY_SYMBOLS[upper] || upper;
};

export const getCurrencyName = (code) => {
  if (!code) return '';
  const upper = String(code).toUpperCase();
  return CURRENCY_NAMES[upper] || upper;
};

/** { code, symbol, name } for a currency code. */
export const describeCurrency = (code) => {
  const upper = String(code || '').toUpperCase();
  return { code: upper, symbol: getCurrencySymbol(upper), name: getCurrencyName(upper) };
};

// ------------------------------------------------------------------
// Rates cache: one entry per base currency
// ------------------------------------------------------------------
let memoryCache = {}; // { GBP: { base, rates, fetchedAt, source } }

const loadCacheFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') memoryCache = parsed;
  } catch (e) {
    memoryCache = {};
  }
};
loadCacheFromStorage();

const saveCacheToStorage = () => {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(memoryCache));
  } catch (e) {
    // storage full / blocked: the in-memory copy still works
  }
};

const fetchJson = async (url) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch(url, { method: 'GET', signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
};

const cleanRates = (data, base) => {
  const raw = data && (data.rates || data.conversion_rates);
  if (!raw || typeof raw !== 'object') return null;

  const rates = {};
  Object.keys(raw).forEach((code) => {
    const value = Number(raw[code]);
    if (Number.isFinite(value) && value > 0) rates[String(code).toUpperCase()] = value;
  });
  rates[base] = 1;
  return Object.keys(rates).length > 1 ? rates : null;
};

/**
 * Latest rates for `baseCurrency`.
 * Returns { base, rates, fetchedAt, source }  where rates[X] = X per 1 base.
 */
export const fetchLatestRates = async (baseCurrency, force = false) => {
  const base = String(baseCurrency || 'USD').toUpperCase();
  const now = Date.now();

  const cached = memoryCache[base];
  if (!force && cached && cached.rates && now - cached.fetchedAt < CACHE_DURATION_MS) {
    return { ...cached, source: 'cache' };
  }

  let rates = null;
  let source = 'primary';

  try {
    rates = cleanRates(await fetchJson(PRIMARY_API(base)), base);
  } catch (primaryErr) {
    console.warn('[ExchangeRate] primary API failed:', primaryErr.message);
  }

  if (!rates) {
    try {
      source = 'fallback';
      rates = cleanRates(await fetchJson(FALLBACK_API(base)), base);
    } catch (fallbackErr) {
      console.warn('[ExchangeRate] fallback API failed:', fallbackErr.message);
    }
  }

  if (!rates) {
    // network is down: an old (expired) copy is better than nothing
    if (cached && cached.rates) return { ...cached, source: 'stale' };
    throw new Error('Could not load exchange rates');
  }

  memoryCache[base] = { base, rates, fetchedAt: now, source };
  saveCacheToStorage();
  return { ...memoryCache[base] };
};

/** Force-refresh the rates of a base currency. */
export const refreshRates = (baseCurrency) => fetchLatestRates(baseCurrency, true);

/** rate(from -> to) from a rates table. 0 when unknown. */
export const rateFromTable = (rates, fromCode, toCode) => {
  const from = String(fromCode || '').toUpperCase();
  const to = String(toCode || '').toUpperCase();
  if (!from || !to) return 0;
  if (from === to) return 1;

  const fromRate = Number(rates?.[from]);
  const toRate = Number(rates?.[to]);
  if (!(fromRate > 0) || !(toRate > 0)) return 0;
  return toRate / fromRate;
};

/** 1 unit of `fromCode` = ? units of `toCode` (fetches rates if needed). */
export const getExchangeRate = async (fromCode, toCode) => {
  const from = String(fromCode || '').toUpperCase();
  const to = String(toCode || '').toUpperCase();
  if (!from || !to) return 1;
  if (from === to) return 1;

  try {
    const { rates } = await fetchLatestRates(from);
    return rateFromTable(rates, from, to) || 1;
  } catch (e) {
    console.warn('[ExchangeRate] getExchangeRate failed:', e.message);
    return 1;
  }
};

export const convertAmount = async (amount, fromCode, toCode) => {
  const rate = await getExchangeRate(fromCode, toCode);
  return Number(amount || 0) * rate;
};

export const formatPrice = (amount, currencyCode) => {
  const symbol = getCurrencySymbol(currencyCode);
  const num = Number(amount || 0);
  return `${symbol}${num.toFixed(2)}`;
};